import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

const EMPTY = { name: "", scope: "global", scope_id: "", type: "block", ips: "" };

export default function ACLsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/acls").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      const ips = String(form.ips)
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/acls", { ...form, ips });
      toast.success(t("common.saved"));
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };
  const del = async (a) => {
    const _ok = await confirm({ title: t("confirm.deleteAcl", { name: a.name }), danger: true, confirmLabel: t("common.delete") || "Delete" });
    if (!_ok) return;
    await api.delete(`/acls/${a.id}`);
    load();
  };

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (open) {
    return (
      <InlineEditorShell
        crumb={t("pages.acls.title")}
        title={t("pages.acls.dialogTitle")}
        onCancel={() => {
          setOpen(false);
          setForm(EMPTY);
        }}
        onSave={save}
        saveDisabled={!form.name?.trim()}
        testidRoot="acl-editor"
      >
        <div className="space-y-3">
          <div>
            <Label>{t("fields.name")}</Label>
            <Input
              data-testid="acl-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("fields.scope")}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
              >
                <option value="global">global</option>
                <option value="customer">customer</option>
                <option value="trunk">trunk</option>
              </select>
            </div>
            <div>
              <Label>{t("fields.type")}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="block">block</option>
                <option value="allow">allow</option>
              </select>
            </div>
          </div>
          <div>
            <Label>{t("fields.ips")}</Label>
            <Input
              data-testid="acl-ips"
              value={form.ips}
              onChange={(e) => setForm({ ...form, ips: e.target.value })}
              placeholder="1.2.3.4 5.6.7.8"
            />
          </div>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-acls">
      <PageHeader
        title={t("pages.acls.title")}
        subtitle={t("pages.acls.subtitle")}
        icon={ListChecks}
        actions={
          <Button onClick={() => setOpen(true)} data-testid="new-acl-btn">
            <Plus className="h-4 w-4 mr-1" /> {t("pages.acls.newBtn")}
          </Button>
        }
      />
      {items.length === 0 ? (
        <EmptyState
          title={t("pages.acls.emptyTitle")}
          description={t("pages.acls.emptyDesc")}
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> {t("pages.acls.emptyAction")}
            </Button>
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead>{t("fields.name")}</TableHead>
                <TableHead>{t("pages.acls.colScope")}</TableHead>
                <TableHead>{t("pages.acls.colType")}</TableHead>
                <TableHead>{t("pages.acls.colIps")}</TableHead>
                <TableHead className="w-[100px] text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} className="row-hover" data-testid={`acl-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>
                    <StatusPill kind="info">{a.scope}</StatusPill>
                  </TableCell>
                  <TableCell>
                    <StatusPill kind={a.type === "block" ? "danger" : "ok"}>
                      {a.type.toUpperCase()}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {(a.ips || []).slice(0, 5).join(", ")}
                    {a.ips?.length > 5 && (
                      <span className="text-muted-foreground"> +{a.ips.length - 5}</span>
                    )}
                    {(!a.ips || a.ips.length === 0) && (
                      <span className="text-muted-foreground">{t("common.empty")}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-[hsl(var(--danger))]"
                      onClick={() => del(a)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
